import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import api, { API, formatApiError } from "@/lib/api";
import Brand from "@/components/Brand";
import TelegramGroupCard from "@/components/TelegramGroupCard";
import { CheckCircle2, XCircle, Loader2, Sparkles, History as HistoryIcon, Calendar, ArrowRight } from "lucide-react";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const billcode = params.get("billcode") || "";
  const [tx, setTx] = useState(null);
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [polling, setPolling] = useState(true);

  const isLoggedIn = !!localStorage.getItem("participant_token");

  useEffect(() => {
    if (!billcode) {
      setError("Tiada order number dalam URL");
      setPolling(false);
      return;
    }
    let stop = false;
    let tries = 0;
    const tick = async () => {
      try {
        const txResp = await axios.get(`${API}/payments/${billcode}/status`);
        if (stop) return;
        setTx(txResp.data);
        if (isLoggedIn) {
          try {
            const meResp = await api.get("/participant/me");
            if (!stop) setMe(meResp.data);
          } catch (_) {
            // ignore - token might be stale
          }
        }
        if (txResp.data?.status === "SUCCESS") {
          setPolling(false);
          return;
        }
      } catch (err) {
        if (stop) return;
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
      tries += 1;
      if (tries < 10 && !stop) {
        setTimeout(tick, 2500);
      } else {
        setPolling(false);
      }
    };
    tick();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billcode]);

  const isSuccess = tx?.status === "SUCCESS";
  const isFailed = tx?.status === "FAILED";
  const showLoginCta = isSuccess && !isLoggedIn && tx?.user_email;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {!isLoggedIn && (
        <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
            <Brand size="sm" showTagline={false} />
          </div>
        </header>
      )}

      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-8 text-center">
          <div className="flex justify-center mb-6">
            {polling && !isSuccess ? (
              <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <Loader2 size={32} className="text-amber-400 animate-spin" />
              </div>
            ) : isSuccess ? (
              <div className="w-16 h-16 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center" data-testid="result-success-icon">
                <CheckCircle2 size={36} className="text-amber-400" />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center" data-testid="result-failed-icon">
                <XCircle size={36} className="text-red-400" />
              </div>
            )}
          </div>

          {polling && !isSuccess ? (
            <>
              <h1 className="font-display text-3xl font-bold mb-2">Mengesahkan Bayaran…</h1>
              <p className="text-sm text-neutral-400">Sistem sedang verify dengan BayarCash. Ini biasanya ambil masa beberapa saat.</p>
            </>
          ) : isSuccess ? (
            <>
              <h1 className="font-display text-3xl font-bold mb-2">Pembayaran Berjaya! 🎉</h1>
              <p className="text-sm text-neutral-400">
                {tx?.auto_enrolled
                  ? `Emel ${tx.user_email} telah didaftarkan secara automatik. Sila log masuk untuk mula menjana prompt.`
                  : "Terima kasih. Akses anda dah diaktifkan."}
              </p>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl font-bold mb-2">Bayaran Tidak Lengkap</h1>
              <p className="text-sm text-neutral-400">
                Kami belum dapat mengesan bayaran berjaya. Jika anda sudah bayar, sila tunggu sebentar atau hubungi kami.
              </p>
            </>
          )}

          {tx && (
            <div className="mt-6 grid grid-cols-2 gap-3 text-left">
              <div className="bg-[#0A0A0A] border border-white/10 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono">Emel</div>
                <div className="text-sm text-white font-mono break-all">{tx.user_email}</div>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono">Pakej</div>
                <div className="text-sm text-white">{tx.tier_label}</div>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono">Jumlah</div>
                <div className="text-sm text-amber-400 font-bold">RM{tx.price_rm}</div>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono">Status</div>
                <div className={`text-sm font-mono ${isSuccess ? "text-amber-400" : isFailed ? "text-red-400" : "text-neutral-400"}`}>
                  {tx.status}
                </div>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono">Order No.</div>
                <div className="text-xs text-neutral-300 font-mono">{tx.billcode}</div>
              </div>
              {tx.extended_to !== undefined && (
                <div className="bg-[#0A0A0A] border border-amber-500/30 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wider text-amber-500/80 font-mono">Akses Tamat</div>
                  <div className="text-sm text-amber-300 font-mono font-bold">
                    {tx.extended_to || "∞ Selamanya"}
                  </div>
                </div>
              )}
            </div>
          )}

          {isSuccess && me && (
            <div className="mt-6 p-4 rounded-lg bg-amber-500/5 border border-amber-500/30 flex items-center gap-3 justify-center">
              <Calendar size={18} className="text-amber-400" />
              <div className="text-sm">
                <span className="text-neutral-400">Tarikh tamat akses anda: </span>
                <span className="text-amber-300 font-mono font-bold">
                  {me.expiry_date || "∞ Selamanya"}
                </span>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-4 py-2">
              {error}
            </div>
          )}

          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            {showLoginCta ? (
              <Link
                to="/login"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="goto-login-btn"
              >
                Log Masuk Sekarang <ArrowRight size={16} />
              </Link>
            ) : (
              <>
                <Link to="/generator" className="btn-primary inline-flex items-center gap-2" data-testid="goto-generator-btn">
                  <Sparkles size={16} /> Mula Jana Prompt
                </Link>
                {isLoggedIn && (
                  <Link
                    to="/transactions"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-white/10 text-sm text-neutral-300 hover:bg-white/5"
                    data-testid="goto-transactions-btn"
                  >
                    <HistoryIcon size={16} /> Sejarah Transaksi
                  </Link>
                )}
              </>
            )}
          </div>
        </div>

        {/* Telegram Group invite — only for logged-in active users */}
        {isSuccess && isLoggedIn && (
          <div className="mt-6">
            <TelegramGroupCard />
          </div>
        )}
      </div>
    </div>
  );
}
